import React, {useCallback, useMemo, useState} from 'react';

import {FormInput} from 'src/components/input';
import {createSearchTemplate} from 'src/openapi/admin/apis/SearchTemplateApi';
import {TemplateType} from 'src/openapi/admin/models/TemplateType';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';


import type {SearchType} from 'src/openapi/admin/models/SearchType';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

type Props = {
    userId: number,
    templates: UserSearchTemplate[],
    onClose: () => void,
    searchType: SearchType,
    onDone: (newTemplate: UserSearchTemplate) => void,
    onFail: (error: string) => void,
    preselectedTemplateId?: number | null,
};

const MODAL_WIDTH = 300;

export const AddTemplateModal = (props: Props) => {
    const {userId, onClose, templates, searchType, onDone, onFail, preselectedTemplateId = null} = props;
    const apiDispatch = useAppDispatch();
    const [templateName, setTemplateName] = useState('');
    const [fromTemplateId, setFromTemplateId] = useState<number | string | null>(preselectedTemplateId);
    const [busy, setBusy] = useState(false);

    const error = useMemo(() => (
        templates.some(template =>
            searchType === template?.searchType &&
            template.templateName.toLocaleLowerCase() === templateName.toLocaleLowerCase(),
        ) ? 'Layout with this name already exists' : undefined
    ), [templateName, templates, searchType]);

    const addTemplate = useCallback(async () => {
        if (!templateName || fromTemplateId === null) {
            return;
        }

        const selectedTemplate = templates.find(template => template.templateId === +fromTemplateId);

        if (selectedTemplate) {
            setBusy(true);
            const response = await apiDispatch(createSearchTemplate({
                updateTemplateRequest: {
                    userId,
                    templateName,
                    defaultSortColumn: selectedTemplate.defaultSortColumn,
                    defaultSortDirection: selectedTemplate.defaultSortDirection,
                    searchType: selectedTemplate.searchType,
                    templateType: TemplateType.CUSTOM,
                    columns: selectedTemplate.columns,
                },
            }));
            setBusy(false);

            if (response.payload.success) {
                const newTemplateId = response.payload.data.templateId;
                const newTemplate = {
                    ...selectedTemplate,
                    templateName,
                    templateId: newTemplateId,
                };

                onDone(newTemplate);
            } else {
                onFail(response.payload.data.error || 'Failed to create layout');
            }
        }
    }, [templateName, fromTemplateId, onDone, templates, apiDispatch, onFail, userId]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Add Layout
            </DialogHeader>
            <DialogMain style={{padding: 24, maxWidth: MODAL_WIDTH}}>
                <Group direction="v">
                    <FormInput
                        width="100%"
                        value={templateName}
                        onBlur={setTemplateName}
                        maxLength={64}
                        label="Layout Name"
                        valid={error ? false : undefined}
                        errorDescription={error}
                        placeholder="Enter layout name"
                    />
                    <div style={{width: '100%'}}>
                        <Label>Create From Layout</Label>
                        <ComboBox value={fromTemplateId} onChange={setFromTemplateId} placeholder="Select layout">
                            {templates
                                .filter(template => template.searchType === searchType)
                                .sort((a, b) => a.templateName.localeCompare(b.templateName, undefined, {numeric: true, sensitivity: 'base'}))
                                .sort((a, b) => b.templateType.localeCompare(a.templateType))
                                .map(template => (
                                    <ComboboxItem
                                        key={template.templateId}
                                        value={template.templateId}
                                        caption={template.templateName}
                                    />
                                ))}
                        </ComboBox>
                    </div>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button
                        busy={busy}
                        variant="Type.PRIMARY"
                        onClick={addTemplate}
                        disabled={!templateName || fromTemplateId === null || !!error}>
                        Add
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
